import {
  DEFAULT_OG_IMAGE_HEIGHT,
  DEFAULT_OG_IMAGE_WIDTH,
  type PelgoSeoConfig,
  toAbsoluteUrl,
} from '@/lib/pelgo-seo'
import { useLayoutEffect } from 'react'

type MetaDescriptor = {
  attr: 'name' | 'property'
  key: string
  content: string
}

function upsertMeta({ attr, key, content }: MetaDescriptor) {
  const selector = `meta[${attr}="${key}"]`
  let element = document.querySelector(selector)

  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attr, key)
    document.head.appendChild(element)
  }

  element.setAttribute('content', content)
}

function upsertCanonical(href: string) {
  let element = document.querySelector('link[rel="canonical"]')

  if (!element) {
    element = document.createElement('link')
    element.setAttribute('rel', 'canonical')
    document.head.appendChild(element)
  }

  element.setAttribute('href', href)
}

type PageMetaProps = {
  config: PelgoSeoConfig
}

export function PageMeta({ config }: PageMetaProps) {
  useLayoutEffect(() => {
    const previousTitle = document.title
    document.title = config.title

    const canonicalUrl = config.canonicalPath ? toAbsoluteUrl(config.canonicalPath) : null
    const ogImageUrl = toAbsoluteUrl(config.ogImagePath ?? '/logo-black.png')
    const ogType = config.ogType ?? 'website'
    const ogImageWidth = config.ogImageWidth ?? DEFAULT_OG_IMAGE_WIDTH
    const ogImageHeight = config.ogImageHeight ?? DEFAULT_OG_IMAGE_HEIGHT

    const descriptors: MetaDescriptor[] = [
      { attr: 'name', key: 'description', content: config.description },
      { attr: 'property', key: 'og:title', content: config.title },
      { attr: 'property', key: 'og:description', content: config.description },
      { attr: 'property', key: 'og:type', content: ogType },
      { attr: 'property', key: 'og:image', content: ogImageUrl },
      { attr: 'property', key: 'og:image:width', content: String(ogImageWidth) },
      { attr: 'property', key: 'og:image:height', content: String(ogImageHeight) },
      { attr: 'property', key: 'og:locale', content: 'en_US' },
      { attr: 'property', key: 'og:site_name', content: 'Pelgo' },
      { attr: 'name', key: 'twitter:card', content: 'summary_large_image' },
      { attr: 'name', key: 'twitter:title', content: config.title },
      { attr: 'name', key: 'twitter:description', content: config.description },
      { attr: 'name', key: 'twitter:image', content: ogImageUrl },
    ]

    if (canonicalUrl) {
      descriptors.push({ attr: 'property', key: 'og:url', content: canonicalUrl })
    }

    if (config.publishedAt && ogType === 'article') {
      descriptors.push({
        attr: 'property',
        key: 'article:published_time',
        content: config.publishedAt,
      })
    }

    for (const descriptor of descriptors) {
      upsertMeta(descriptor)
    }

    if (canonicalUrl) {
      upsertCanonical(canonicalUrl)
    } else {
      document.querySelector('link[rel="canonical"]')?.remove()
      document.querySelector('meta[property="og:url"]')?.remove()
    }

    return () => {
      document.title = previousTitle
    }
  }, [
    config.title,
    config.description,
    config.canonicalPath,
    config.ogImagePath,
    config.ogImageWidth,
    config.ogImageHeight,
    config.ogType,
    config.publishedAt,
  ])

  return null
}
